import { useCommandState } from 'cmdk';
import pluralize from 'pluralize';
import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import AccountLogo from 'components/accounts/AccountLogo';
import { CommandGroup } from 'components/commandPalette/CommandGroup';
import type { SearchResultType } from 'components/globalSearch/types';
import Emoji from 'components/lib/ui/Emoji';
import Icon from 'components/lib/ui/Icon';
import Text from 'components/lib/ui/Text';

import useDebounce from 'common/lib/hooks/useDebounce';
import useQuery from 'common/lib/hooks/useQuery';
import { capitalize, isEmoji } from 'common/utils/String';
import { useCommandRegistry, useGetCommandsByGroup } from 'lib/commands/context';
import { getRouteForResultType } from 'lib/globalSearch/routes';

import { gql } from 'common/generated';

const ICON_SIZE_PX = 16;
const SEARCH_DEBOUNCE_MS = 300;

type ItemIconProps = {
  icon: Maybe<string>;
  logoUrl: Maybe<string>;
};

/**
 * A generic component that renders commands for a given entity type.
 */
export const DynamicEntityCommands = () => {
  const commands = useGetCommandsByGroup('entities');
  const search = useCommandState((state) => state.search);
  const isSearchEmpty = search.length === 0;

  const commandsByEntityType = useMemo(
    () =>
      commands.reduce(
        (acc, command) => {
          if (command.type) {
            acc[command.type] = [...(acc[command.type] || []), command];
          }
          return acc;
        },
        {} as Record<string, typeof commands>,
      ),
    [commands],
  );

  return (
    // We need to wrap this in a Fragment because React thinks the
    // `map` returns more than one child (Element[] instead of Element).
    <>
      {Object.entries(commandsByEntityType).map(([entityType, commands]) => (
        <CommandGroup.Root
          key={entityType}
          group="entities"
          heading={capitalize(pluralize(entityType))}
          rightContent={
            isSearchEmpty && (
              <Text weight="book" size="xsmall">
                most recent searches
              </Text>
            )
          }
        >
          {commands.map((command) => (
            <CommandGroup.Item key={command.id} {...command} />
          ))}
        </CommandGroup.Root>
      ))}
    </>
  );
};

const ItemIcon = React.memo(({ icon, logoUrl }: ItemIconProps) => {
  if (icon) {
    return isEmoji(icon) ? <Emoji>{icon}</Emoji> : <Icon name={icon} />;
  }

  return <AccountLogo logoUrl={logoUrl} size={ICON_SIZE_PX} />;
});

/**
 * A hook that registers commands for entities based on a search term.
 *
 * Check the other comments for more context.
 */
export const useRegisterEntityCommands = (search: string) => {
  const { registerCommand } = useCommandRegistry();
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const history = useHistory();

  const { data, isNetworkRequestInFlight } = useQuery(GET_COMMAND_PALETTE_ENTITIES, {
    variables: { searchTerm: debouncedSearch || '' },
    fetchPolicy: 'cache-first',
  });

  const { results } = data?.globalSearch || {};

  useEffect(() => {
    if (results) {
      results.forEach((result) =>
        registerCommand({
          id: result.id,
          label: result.name,
          icon: <ItemIcon icon={result.icon} logoUrl={result.logoUrl} />,
          group: 'entities',
          // This is what declares what entity type the command is for.
          // The values come from the backend (merchant/category/account/etc).
          type: result.type,
          keywords: [result.name, result.type],
          action: () =>
            history.push(getRouteForResultType(result.type as SearchResultType, result.id)),
        }),
      );
    }
  }, [results, registerCommand, history]);

  return { isLoading: isNetworkRequestInFlight };
};

const GET_COMMAND_PALETTE_ENTITIES = gql(/* GraphQL */ `
  query Web_GetCommandPaletteEntities($searchTerm: String!) {
    globalSearch(searchTerm: $searchTerm) {
      searchTerm
      results {
        id
        name
        icon
        logoUrl
        type
      }
    }
  }
`);
